import { spreadAndtransformDefaultValues } from '@common/components/forms';
import { PageCard } from '@common/components/layout';
import { YesNoConfirmModal } from '@common/components/modals';
import { TableSkeleton } from '@common/components/skeleton/TableSkeleton';
import { SuspenseButton } from '@common/components/utility';
import { useApp } from '@common/hooks';
import { UserModel, VeterinarianModel } from '@common/models';
import { FeatureFlags } from '@common/models/Enums';
import { AuthOperationUINames } from '@common/models/Enums/AuthOperationStrings';
import { NotificationStoreInstance, ProactivePrescriptionRuleProvider } from '@common/stores';
import { ProactivePrescriptionDisabledBanner } from '@common/views/proactivePrescriptions';
import { SignatureStore, VeterinarianStore } from '@hospital/stores';

import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';

import { UserLocations, UserProactivePrescriptions, UserProfileDetail, UserProfileLicenses, UserProfileRoles, UserProfileSignature, UserSchedulePreferences } from '.';
import { UserInactiveProfileBanner } from './UserInactiveProfileBanner';
import { UserProfileCommunicationTemplates } from './UserProfileCommunicationTemplates';
import { UserStore } from './UserStore';
import { UserFlags } from './UserFlags';

export interface IUserProfileProps {
  store: UserStore;
  vetStore: VeterinarianStore;
}

export const UserProfile = observer(({ store, vetStore }: IUserProfileProps) => {
  const { configuration } = useApp();
  const [editing, setEditing] = useState<boolean>(false);
  const [editingUserFlags, setEditingUserFlags] = useState<boolean>(false);
  const [localVet, setLocalVet] = useState<VeterinarianModel | undefined>(undefined);
  const [confirmDialog, setConfirmDialog] = useState<{ description: string, visible: boolean, size: 'sm' | 'lg' | 'xl' | undefined }>({
    description: '',
    visible: false,
    size: undefined
  });
  const [flagsConfirm, setFlagsConfirm] = useState<boolean>(false);
  const signatureStore = new SignatureStore({ userStore: store });
  const currentUserIsThisVet = vetStore.veterinarian?.petcoUserId == configuration.userIdentityName.split('@')[0];
  const canEditUserRoles = configuration.HasUIFlag(AuthOperationUINames.UI_HospitalFlags_CanEditUserRoles);
  const canEditUserLocations = configuration.HasUIFlag(AuthOperationUINames.UI_HospitalFlags_CanEditUserLocations);
  const canEditDvmLicenses = currentUserIsThisVet || configuration.HasUIFlag(AuthOperationUINames.UI_HospitalFlags_CanEditDvmLicenses);
  const canEditDvmSchedule = currentUserIsThisVet || configuration.HasUIFlag(AuthOperationUINames.UI_HospitalFlags_CanEditDvmSchedule);
  const canEditDvmSignature = currentUserIsThisVet || configuration.HasUIFlag(AuthOperationUINames.UI_HospitalFlags_CanEditDvmSignature);
  const canEditProviderCommunicationTemplates = currentUserIsThisVet || configuration.HasUIFlag(AuthOperationUINames.UI_HospitalFlags_CanEditProviderCommunicationTemplates)

  const formMethods = useForm<VeterinarianModel>({ defaultValues: localVet })
  const { reset, getValues, handleSubmit } = formMethods;

  useEffect(() => {
    if (store.vet?.veterinarianId) {
      vetStore.fetchVeterinarianByVetId(store.vet.veterinarianId)
        .then(() => {
          const clone = _.cloneDeep(vetStore.veterinarian);
          clone!.optOutOfProactivePrescription = !vetStore.veterinarian?.optOutOfProactivePrescription;
          clone!.overrideHospitalProactivePrescriptions = !vetStore.veterinarian?.overrideHospitalProactivePrescriptions;
          setLocalVet(clone);
        });
    }
  }, [store.vet, vetStore]);

  useEffect(() => {
    if (vetStore.veterinarian) {
      reset(localVet)
    }
  }, [localVet, reset, vetStore.veterinarian])

  const onSubmit = (vetData: VeterinarianModel) => {
    const vet = vetStore.veterinarian;
    if (!vet) return;

    const updatedVet: VeterinarianModel = {
      ...vet,
      optOutOfProactivePrescription:
        vetData.optOutOfProactivePrescription !== !vet.optOutOfProactivePrescription
          ? !vetData.optOutOfProactivePrescription
          : vet.optOutOfProactivePrescription,

      overrideHospitalProactivePrescriptions:
        vetData.overrideHospitalProactivePrescriptions !== !vet.overrideHospitalProactivePrescriptions
          ? !vetData.overrideHospitalProactivePrescriptions
          : vet.overrideHospitalProactivePrescriptions
    } as VeterinarianModel;

    vetStore.saveVeterinarian(updatedVet)
      .then(() => {
        const newLocalVet = {
          ...updatedVet,
          optOutOfProactivePrescription: !updatedVet.optOutOfProactivePrescription,
          overrideHospitalProactivePrescriptions: !updatedVet.overrideHospitalProactivePrescriptions
        } as VeterinarianModel;
        setLocalVet(newLocalVet);
        reset(newLocalVet);
        setEditing(false);
        NotificationStoreInstance.success('Veterinarian updated successfully');
      });
  }

  const onReset = () => {
    reset({
      optOutOfProactivePrescription: !vetStore.veterinarian?.optOutOfProactivePrescription,
      overrideHospitalProactivePrescriptions: !vetStore.veterinarian?.overrideHospitalProactivePrescriptions
    });
    setEditing(false);
  }

  const saveDetailsHandler = () => {
    const optOutFormValue = !getValues('optOutOfProactivePrescription');
    const overrideFormValue = !getValues('overrideHospitalProactivePrescriptions');

    // If allow proactive prescribing changes
    if (optOutFormValue !== vetStore.veterinarian?.optOutOfProactivePrescription) {
      if (optOutFormValue === true) {
        setConfirmDialog({
          ...confirmDialog,
          description: 'You have opted out of allowing PetPass to proactively prescribe medications for eligible pets. Are you sure you want to continue?',
          size: undefined,
          visible: true
        });
      } else {
        const markup = 'You have opted into allowing PetPass to proactively prescribe medications for eligible pets. Are you sure you want to continue?'
          + (overrideFormValue
            ? ('<br/><br/>You have opted out of using suggested medications to proactively prescribe medications for eligible pets. To continue leveraging proactive prescribing functionality, you must set your own suggested medications <b>for each rule and species</b> you want PetPass to support.'
              + '<br/><br/>If a preference is not configured, <b>PetPass will not proactively prescribe</b> a medication for that rule. Are you sure you want to continue?')
            : '<br/><br/>You have opted into using suggested medications to proactively prescribe medications for eligible pets.');
        setConfirmDialog({
          ...confirmDialog,
          description: markup,
          size: vetStore.veterinarian?.overrideHospitalProactivePrescriptions ? 'lg' : undefined,
          visible: true
        });
      }

      return;
    }

    // If allow petco prefered medications changes
    if (overrideFormValue !== vetStore.veterinarian?.overrideHospitalProactivePrescriptions) {
      if (overrideFormValue === true) {
        const markup = 'You have opted out of using suggested medications to proactively prescribe medications for eligible pets. To continue leveraging proactive prescribing functionality, you must set your own suggested medications <b>for each rule and species</b> you want PetPass to support.'
          + '<br/><br/>If a preference is not configured, <b>PetPass will not proactively prescribe</b> a medication for that rule. Are you sure you want to continue?';
        setConfirmDialog({ ...confirmDialog, description: markup, size: 'lg', visible: true });
      } else {
        const markup = optOutFormValue
          ? 'You have opted out of allowing PetPass to proactively prescribe medications for eligible pets. Are you sure you want to continue?'
          : 'You have opted into using suggested medications to proactively prescribe medications for eligible pets.';
        setConfirmDialog({ ...confirmDialog, description: markup, size: undefined, visible: true });
      }

      return;
    }

    onConfirmChange();
  }

  const onConfirmChange = () => {
    handleSubmit(onSubmit)();
    setConfirmDialog({ ...confirmDialog, visible: false });
  }

  const flagsForm = useForm<UserModel>({
    defaultValues: spreadAndtransformDefaultValues(store.user)
  });

  const flagsSaveHandler = () => {
    const value = flagsForm.getValues("optInToAudioRecording");

    if (value === true)
      setFlagsConfirm(true);
    else
      flagsConfirmHandler();
  }

  const flagsCancelHandler = () => {
    flagsForm.reset(spreadAndtransformDefaultValues(store.user));
    setEditingUserFlags(false);
  }

  const flagsConfirmHandler = () => {
    flagsForm.handleSubmit((data) => {
      store.updateUserFlags(data)
        .then(() => {
          NotificationStoreInstance.success('User flags updated successfully');
          setFlagsConfirm(false);
          setEditingUserFlags(false);
        });
    })();
  }

  return (
    <>
      <Row>
        <Col md={12}>
          <UserInactiveProfileBanner isProfileActive={store.user?.isProfileActive} />
        </Col>
      </Row>
      <Row>
        <Col md={7}>
          <div className="f-flex flex-column">
            <PageCard title="User Details">
              <UserProfileDetail user={store.user} isVeterinarian={store.hasVetRole} />
            </PageCard>
            <PageCard className="mt-md-4" title="Roles">
              <UserProfileRoles store={store} readOnly={!canEditUserRoles} />
            </PageCard>

            {store.hasVetRole && !vetStore.loading && <>
              <PageCard className="mt-md-4" title="Licenses and Registrations">
                <UserProfileLicenses store={store} readOnly={!canEditDvmLicenses} />
              </PageCard>
              <PageCard className="mt-md-4" title="Schedule Preferences">
                {vetStore.loading && <TableSkeleton rows={12} cols={7} />}
                {!vetStore.loading && vetStore.veterinarian &&
                  <UserSchedulePreferences
                    readOnly={!canEditDvmSchedule}
                    scheduleBlockTypes={configuration.scheduleBlockTypes.filter(e => e.isOverrideType === false)}
                    veterinarian={vetStore.veterinarian}
                    onSavePreferences={(schedulePreferences, schedulingColor) => vetStore.saveSchedulePreferences({ schedulePreferences, schedulingColor })}
                    isActive={store.isActive} />
                }
              </PageCard>
            </>}
          </div>
        </Col>
        <Col md={5}>
          <FormProvider {...formMethods}>
            <Form>
              <div className="f-flex flex-column ps-4">
                <PageCard title="Locations">
                  <UserLocations store={store} readOnly={!canEditUserLocations} />
                </PageCard>

                {configuration.hasFeatureFlag(FeatureFlags.AllowScribenoteCapture) && <PageCard
                  className="mt-md-4"
                  title="User Flags"
                  actionElement={editingUserFlags
                    ? <div className='d-flex flex-row'>
                      <Button className='btn-outline-primary btn btn-secondary me-2' onClick={flagsCancelHandler}>Cancel</Button>
                      <SuspenseButton variant='primary' onClick={flagsSaveHandler} tag="updateUserFlags">Save Details</SuspenseButton>
                    </div>
                    : <Button variant='primary' onClick={() => setEditingUserFlags(!editingUserFlags)}>Edit Details</Button>
                  }>
                  <FormProvider {...flagsForm}>
                    <UserFlags
                      isEditing={editingUserFlags}
                      showModal={flagsConfirm}
                      onCancel={() => setFlagsConfirm(false)}
                      onConfirm={flagsConfirmHandler} />
                  </FormProvider>
                </PageCard>}

                {store.hasVetRole && !vetStore.loading &&
                  <PageCard className="mt-md-4" title="Signature">
                    <UserProfileSignature store={signatureStore} readOnly={!canEditDvmSignature} isActive={store.isActive} />
                  </PageCard>
                }

                {store.hasVetRole && !vetStore.loading &&
                  <PageCard className="mt-md-4" title="Hospital Communication Templates">
                    <UserProfileCommunicationTemplates store={store} readOnly={!canEditProviderCommunicationTemplates} />
                  </PageCard>
                }

                {store.hasVetRole && !vetStore.loading && configuration.hasFeatureFlag(FeatureFlags.AllowProactivePrescriptionsHospitals) &&
                  <div>
                    <PageCard className="mt-md-4" title="Proactive Prescriptions" topBanner={vetStore.veterinarian?.optOutOfProactivePrescription ? <ProactivePrescriptionDisabledBanner /> : <></>} actionElement={
                      editing
                        ? <div className='d-flex flex-row'>
                          <Button className='btn-outline-primary btn btn-secondary me-2' onClick={() => { setEditing(false); onReset(); }}>Cancel</Button>
                          <SuspenseButton variant='primary' onClick={saveDetailsHandler} tag='saveVeterinarian'>Save Details</SuspenseButton>
                        </div>
                        : <Button variant='primary' onClick={() => setEditing(!editing)}>Edit Details
                        </Button>
                    }>
                      <ProactivePrescriptionRuleProvider>
                        <UserProactivePrescriptions currentOverrideHospitalProactivePrescriptions={vetStore.veterinarian?.overrideHospitalProactivePrescriptions} isEditing={editing} />
                      </ProactivePrescriptionRuleProvider>
                    </PageCard>
                  </div>

                }
              </div>
            </Form>
          </FormProvider>
        </Col>
      </Row >
      {confirmDialog.visible &&
        <YesNoConfirmModal
          visible={confirmDialog.visible}
          size={confirmDialog.size}
          busy={false}
          title="Confirm proactive prescription settings"
          description={<div dangerouslySetInnerHTML={{ __html: confirmDialog.description }} />}
          onConfirm={() => onConfirmChange()}
          onCancel={() => setConfirmDialog({ ...confirmDialog, visible: false })}
        />
      }
    </>
  )
});
